import { cn } from "../lib/utils";

export type OrderBookDisplaySide = {
  trader: string;
  qty: string;
  price: string;
  hasValue: boolean;
};

export type OrderBookDisplayRow = {
  key: string;
  bid: OrderBookDisplaySide;
  ask: OrderBookDisplaySide;
};

type OrderBookRowsProps = {
  rows: OrderBookDisplayRow[];
  showLabels?: boolean;
  withRowBorders?: boolean;
  onBidClick?: () => void;
  onAskClick?: () => void;
  disabled?: boolean;
  disabledTitle?: string;
  className?: string;
  bidLabel?: string;
  askLabel?: string;
  showSideLabelColumns?: boolean;
};

const sideShellClassName =
  "flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden rounded px-1.5 py-1 md:px-2";
const sideButtonClassName =
  `${sideShellClassName} transition-all duration-150`;
const bidGridClassName =
  "grid w-full min-w-0 grid-cols-[minmax(0,1fr)_2.25rem_0.5rem_minmax(2.5rem,max-content)] items-baseline gap-x-1 md:grid-cols-[minmax(0,1fr)_2.5rem_0.5rem_minmax(2.75rem,max-content)] lg:grid-cols-[minmax(72px,1fr)_3.5rem_0.75rem_auto] lg:gap-x-1.5";
const askGridClassName =
  "grid w-full min-w-0 grid-cols-[minmax(2.5rem,max-content)_0.5rem_2.25rem_minmax(0,1fr)] items-baseline gap-x-1 md:grid-cols-[minmax(2.75rem,max-content)_0.5rem_2.5rem_minmax(0,1fr)] lg:grid-cols-[auto_0.75rem_3.5rem_minmax(72px,1fr)] lg:gap-x-1.5";
const traderClassName =
  "min-w-0 truncate font-display text-[11px] font-bold uppercase tracking-[0.04em] md:text-xs lg:text-sm";
const quantityClassName =
  "min-w-0 font-mono text-sm font-semibold tabular-nums md:text-base lg:text-lg";
const priceClassName =
  "min-w-0 font-mono text-xl font-bold leading-none tracking-normal tabular-nums md:text-2xl lg:text-2xl xl:text-3xl";

export function OrderBookRows({
  rows,
  showLabels = false,
  withRowBorders = false,
  onBidClick,
  onAskClick,
  disabled = false,
  disabledTitle,
  className,
  bidLabel = "BID",
  askLabel = "ASK",
  showSideLabelColumns = false,
}: OrderBookRowsProps) {
  const renderBidSide = (row: OrderBookDisplayRow) => {
    const sideContent = (
      <>
        <div className={bidGridClassName}>
          <span
            className={cn(
              traderClassName,
              "text-left",
              row.bid.hasValue ? "text-foreground-muted" : "text-foreground-subtle",
            )}
          >
            {row.bid.hasValue ? row.bid.trader : ""}
          </span>
          <span
            className={cn(
              quantityClassName,
              "text-right",
              row.bid.hasValue ? "text-foreground/85" : "text-foreground-subtle",
            )}
          >
            {row.bid.hasValue ? row.bid.qty : ""}
          </span>
          <span className="text-center text-xs text-foreground-faint md:text-sm">{row.bid.hasValue ? "x" : ""}</span>
          <span
            className={cn(
              priceClassName,
              "text-right",
              row.bid.hasValue ? "text-success" : "text-foreground-subtle",
            )}
          >
            {row.bid.price}
          </span>
        </div>
      </>
    );

    if (!onBidClick) {
      return <div className={sideShellClassName}>{sideContent}</div>;
    }

    return (
      <button
        type="button"
        onClick={onBidClick}
        disabled={disabled}
        title={disabled ? disabledTitle : "Click to place a Buy order"}
        className={cn(
          sideButtonClassName,
          !disabled && "cursor-pointer hover:bg-success/10",
          disabled && "cursor-not-allowed",
        )}
      >
        {sideContent}
      </button>
    );
  };

  const renderAskSide = (row: OrderBookDisplayRow) => {
    const sideContent = (
      <>
        <div className={askGridClassName}>
          <span
            className={cn(
              priceClassName,
              "text-left",
              row.ask.hasValue ? "text-danger" : "text-foreground-subtle",
            )}
          >
            {row.ask.price}
          </span>
          <span className="text-center text-xs text-foreground-faint md:text-sm">{row.ask.hasValue ? "x" : ""}</span>
          <span
            className={cn(
              quantityClassName,
              "text-left",
              row.ask.hasValue ? "text-foreground-muted" : "text-foreground-subtle",
            )}
          >
            {row.ask.hasValue ? row.ask.qty : ""}
          </span>
          <span
            className={cn(
              traderClassName,
              "text-right",
              row.ask.hasValue ? "text-foreground-muted" : "text-foreground-subtle",
            )}
          >
            {row.ask.hasValue ? row.ask.trader : ""}
          </span>
        </div>
      </>
    );

    if (!onAskClick) {
      return <div className={sideShellClassName}>{sideContent}</div>;
    }

    return (
      <button
        type="button"
        onClick={onAskClick}
        disabled={disabled}
        title={disabled ? disabledTitle : "Click to place a Sell order"}
        className={cn(
          sideButtonClassName,
          !disabled && "cursor-pointer hover:bg-danger/10",
          disabled && "cursor-not-allowed",
        )}
      >
        {sideContent}
      </button>
    );
  };

  return (
    <div className={cn("w-full", className)}>
      {showLabels && !showSideLabelColumns && (
        <div className="mb-0.5 flex items-center">
          <div className="flex min-w-0 flex-1 px-2">
            <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wide text-success/80 text-left">
              {bidLabel}
            </span>
          </div>
          <span className="w-4 flex-shrink-0" aria-hidden="true" />
          <div className="flex min-w-0 flex-1 justify-end px-2">
            <span className="text-[10px] md:text-xs font-semibold uppercase tracking-wide text-danger/80 text-right">
              {askLabel}
            </span>
          </div>
        </div>
      )}
      {rows.map((row, index) => (
        <div
          key={row.key}
          className={cn(
            showSideLabelColumns
              ? "grid grid-cols-[3.5rem_minmax(0,1fr)_1rem_minmax(0,1fr)_3.5rem] items-center"
              : "flex items-center",
            withRowBorders && index > 0 && "border-t border-border-subtle/20",
          )}
        >
          {showSideLabelColumns && (
            <span className="px-2 text-left font-display text-[11px] font-semibold uppercase tracking-wide text-success md:text-xs">
              {index === 0 && showLabels && showSideLabelColumns ? bidLabel : ""}
            </span>
          )}
          {renderBidSide(row)}
          <span className="w-4 flex-shrink-0 text-center text-foreground-faint">|</span>
          {renderAskSide(row)}
          {showSideLabelColumns && (
            <span className="px-2 text-right font-display text-[11px] font-semibold uppercase tracking-wide text-danger md:text-xs">
              {index === 0 && showLabels && showSideLabelColumns ? askLabel : ""}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
